import {
  buildMailtoUrlFromDraft,
  isCopyableEmailBlock,
  parseEmailDraft,
  rebuildEditedDraft,
  trimBoundaryNewlines,
  type CopyableEmailDraft,
} from '@littlebird/ui-utils';
import { useContext, useLayoutEffect, useRef, useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { SharedMarkdownEditor } from '@/components/ui/editor';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { electronApis } from '@/features/electron-api';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { copyRenderedMarkdown } from '@/utils/clipboard';
import { renderMarkdown } from '@/utils/html';
import { handleExternalUrlClick } from '@/utils/platform';

import { StaticExportContext } from './static-export-context';

export interface CopyableBlockProps {
  content: string;
  label?: string;
  editable: boolean;
  complete: boolean;
  onEdit?: (newText: string) => void;
  onAskSelection?: (selection: { text: string }) => void;
  isCompact?: boolean;
}

/**
 * The editor shows the body without any boundary line breaks (those are
 * carried by `raw` and re-emitted by the rebuild). Leading and trailing
 * spaces/tabs are kept intact so a user can actually edit first-line
 * indentation or trailing whitespace.
 */
function getVisibleContent(content: string, isEmail: boolean): string {
  const source = isEmail ? parseEmailDraft(content).body : content;
  return trimBoundaryNewlines(source ?? '');
}

interface UseUpstreamReconciliationArgs {
  visibleContent: string;
  initialSubject: string;
  complete: boolean;
  onAuthoritativeChange: (body: string, subject: string) => void;
}

/**
 * Owns the "what's the canonical original?" snapshot and decides when an
 * upstream prop change is authoritative (regeneration, history swap,
 * server normalization) vs. just the parent echoing our own onEdit write
 * back at us. On an authoritative change while complete, fires
 * onAuthoritativeChange so the caller can sync local state and remount
 * the editor.
 */
function useUpstreamReconciliation({
  visibleContent,
  initialSubject,
  complete,
  onAuthoritativeChange,
}: UseUpstreamReconciliationArgs) {
  const originalContentRef = useRef<string | null>(
    complete ? visibleContent : null
  );
  const originalSubjectRef = useRef<string | null>(
    complete ? initialSubject : null
  );
  const lastPersistedRef = useRef<{ body: string; subject: string } | null>(
    null
  );
  const onAuthoritativeChangeRef = useRef(onAuthoritativeChange);
  onAuthoritativeChangeRef.current = onAuthoritativeChange;

  useLayoutEffect(() => {
    if (!complete) {
      originalContentRef.current = null;
      originalSubjectRef.current = null;
      lastPersistedRef.current = null;
      return;
    }

    if (originalContentRef.current === null) {
      originalContentRef.current = visibleContent;
      originalSubjectRef.current = initialSubject;
      // visibleContent / initialSubject change shape on the
      // streaming-to-complete flip (email parsing gates on complete),
      // so push the now-final values through to local state.
      onAuthoritativeChangeRef.current(visibleContent, initialSubject);
      return;
    }

    const isOwnEcho =
      lastPersistedRef.current?.body === visibleContent &&
      lastPersistedRef.current?.subject === initialSubject;

    if (isOwnEcho) return;

    if (
      visibleContent === originalContentRef.current &&
      initialSubject === originalSubjectRef.current
    ) {
      return;
    }

    originalContentRef.current = visibleContent;
    originalSubjectRef.current = initialSubject;
    lastPersistedRef.current = null;

    onAuthoritativeChangeRef.current(visibleContent, initialSubject);
  }, [complete, visibleContent, initialSubject]);

  const recordPersisted = (body: string, subject: string) => {
    lastPersistedRef.current = { body, subject };
  };

  return {
    originalContent: originalContentRef.current ?? visibleContent,
    originalSubject: originalSubjectRef.current ?? initialSubject,
    recordPersisted,
  };
}

interface UseCopyableEditorStateArgs {
  visibleContent: string;
  initialSubject: string;
  emailDraft?: CopyableEmailDraft;
  originalNonEmailContent: string;
  complete: boolean;
  canEdit: boolean;
  isEmailAction: boolean;
  onEdit?: (newText: string) => void;
}

function useCopyableEditorState({
  visibleContent,
  initialSubject,
  emailDraft,
  originalNonEmailContent,
  complete,
  canEdit,
  isEmailAction,
  onEdit,
}: UseCopyableEditorStateArgs) {
  const [copyText, setCopyText] = useState(visibleContent);
  const [subjectText, setSubjectText] = useState(initialSubject);
  const [contentVersion, setContentVersion] = useState(0);

  // Sync local state from upstream while the message is still streaming
  // so the editor mounts with the latest text once canEdit flips.
  useLayoutEffect(() => {
    if (complete) return;
    setCopyText(visibleContent);
    setSubjectText(initialSubject);
  }, [complete, visibleContent, initialSubject]);

  const { originalContent, originalSubject, recordPersisted } =
    useUpstreamReconciliation({
      visibleContent,
      initialSubject,
      complete,
      onAuthoritativeChange: (newBody, newSubject) => {
        setCopyText(newBody);
        setSubjectText(newSubject);
        setContentVersion((version) => version + 1);
      },
    });

  const hasUserEdit =
    canEdit &&
    (copyText !== originalContent || subjectText !== originalSubject);

  /**
   * Wraps the user-edited body with the boundary line breaks that
   * `getVisibleContent` stripped from the editor's view. Scoped to line
   * breaks (not all whitespace) so leading or trailing spaces/tabs the
   * user types into the editor land in the source verbatim instead of
   * being silently restored from the original. Falls back to the
   * un-wrapped text when the source is only line breaks - the leading
   * and trailing matches would otherwise overlap and duplicate bytes.
   */
  const reapplyBodyExtras = (text: string, source: string): string => {
    const leading = /^(?:\r\n|\r|\n)+/.exec(source)?.[0] ?? '';
    const trailing = /(?:\r\n|\r|\n)+$/.exec(source)?.[0] ?? '';
    if (leading.length + trailing.length > source.length) return text;
    return leading + text + trailing;
  };

  const buildPersistedContent = (text: string, subject: string) => {
    if (!isEmailAction || !emailDraft) {
      // Non-email payloads expose only the trimmed visible body to the
      // editor; restore the source's leading/trailing extras around the
      // edited text so intentional blank lines aren't dropped on save.
      return reapplyBodyExtras(text, originalNonEmailContent);
    }
    // rebuildEditedDraft reuses the source `Subject:` line (byte-exact) when
    // the subject is unchanged and splices the new value in otherwise, so the
    // cache prefix this layer preserves survives a subject edit.
    const body = reapplyBodyExtras(text, emailDraft.body);
    return rebuildEditedDraft(emailDraft, { subject, body });
  };

  const persistPendingEdit = () => {
    if (!canEdit) return;
    if (copyText === visibleContent && subjectText === initialSubject) return;
    recordPersisted(copyText, subjectText);
    onEdit?.(buildPersistedContent(copyText, subjectText));
  };

  const updateAndPersistText = (text: string) => {
    setCopyText(text);
    if (!canEdit) return;
    recordPersisted(text, subjectText);
    onEdit?.(buildPersistedContent(text, subjectText));
  };

  const reset = () => {
    setCopyText(originalContent);
    setSubjectText(originalSubject);
    setContentVersion((version) => version + 1);
    // Push the revert to the parent if it has any persisted intermediate
    // edits, so its stored content matches what's now in the editor.
    if (
      canEdit &&
      (visibleContent !== originalContent || initialSubject !== originalSubject)
    ) {
      recordPersisted(originalContent, originalSubject);
      onEdit?.(buildPersistedContent(originalContent, originalSubject));
    }
  };

  return {
    copyText,
    setCopyText,
    subjectText,
    setSubjectText,
    contentVersion,
    hasUserEdit,
    persistPendingEdit,
    reset,
    updateAndPersistText,
  };
}

export function CopyableBlock({
  content,
  label,
  editable,
  complete,
  onEdit,
  onAskSelection,
  isCompact = false,
}: CopyableBlockProps) {
  const isStaticExport = useContext(StaticExportContext);
  const blockRef = useRef<HTMLDivElement>(null);
  const canEdit = editable && complete;
  const isEmailAction = complete && isCopyableEmailBlock(label, content);
  const emailDraft = isEmailAction ? parseEmailDraft(content) : undefined;
  const visibleContent = getVisibleContent(content, isEmailAction);
  const initialSubject = emailDraft?.subject ?? '';
  const originalNonEmailContent = isEmailAction ? '' : content;
  const showSubjectRow = isEmailAction && (canEdit || Boolean(initialSubject));
  const headerLabel = showSubjectRow ? undefined : label;
  const bodyAriaLabel = isEmailAction ? 'Body' : (label ?? 'Content');
  const [isCopied, setIsCopied] = useState(false);

  const {
    copyText,
    setCopyText,
    subjectText,
    setSubjectText,
    contentVersion,
    hasUserEdit,
    persistPendingEdit,
    reset,
    updateAndPersistText,
  } = useCopyableEditorState({
    visibleContent,
    initialSubject,
    emailDraft,
    originalNonEmailContent,
    complete,
    canEdit,
    isEmailAction,
    onEdit,
  });
  useLayoutEffect(() => {
    if (!isCopied) return;

    const timer = window.setTimeout(() => setIsCopied(false), 500);
    return () => window.clearTimeout(timer);
  }, [isCopied]);

  const handleEmailAction = () => {
    persistPendingEdit();
    handleExternalUrlClick(
      buildMailtoUrlFromDraft(subjectText, renderMarkdown(copyText).text)
    );
  };

  const handleCopy = async () => {
    persistPendingEdit();
    await copyRenderedMarkdown(copyText);
    setIsCopied(true);
  };

  const handlePaste = async () => {
    if (!isCompact || copyText.length === 0) return;

    trackEvent('HUMMINGBIRD_PASTE_RESPONSE', {
      source: 'copyable_block',
    });
    persistPendingEdit();
    await electronApis.hummingbird.hideHummingbird();
    await electronApis.context.pasteText(renderMarkdown(copyText).text);
  };

  if (!complete && content.length === 0) return null;

  const canOpenEmail = Boolean(subjectText.trim() || copyText.trim());
  const showHeader = showSubjectRow || Boolean(headerLabel) || !isStaticExport;
  const showFooter =
    !isStaticExport && (hasUserEdit || isEmailAction || isCompact);
  return (
    <div
      ref={blockRef}
      className={cn(
        'my-4',
        'not-prose',
        'lb-editor-table-root',
        'relative',
        'flex flex-col overflow-hidden',
        'min-h-[14.375rem]',
        'rounded-2xl smoothing-default shadow-sm',
        { 'bg-grayA-3': isCompact },
        { 'border-border-strong bg-surface': !isCompact },
        'border border-border-strong',
        canEdit && 'focus-within:border-accent',
        'shadow-sm'
      )}
    >
      {showHeader && (
        <XStack
          className={cn(
            'items-center gap-2 border-b px-5 font-sans',
            showSubjectRow ? 'py-2' : 'h-12 text-foreground-subtle',
            isCompact ? 'border-grayA-4' : 'border-border'
          )}
        >
          {showSubjectRow ? (
            <>
              <span className="text-foreground-muted">Subject:&nbsp;</span>
              {canEdit ? (
                <input
                  aria-label="Subject"
                  type="text"
                  value={subjectText}
                  onChange={(e) => setSubjectText(e.target.value)}
                  onBlur={persistPendingEdit}
                  className="min-w-0 flex-1 bg-transparent text-foreground-subtle outline-none"
                />
              ) : (
                <span className="min-w-0 flex-1 truncate text-foreground-subtle">
                  {subjectText}
                </span>
              )}
            </>
          ) : (
            <span className="min-w-0 flex-1 truncate">{headerLabel}</span>
          )}
          {!isStaticExport && (
            <Button
              aria-label="Copy text"
              className="bg-surface"
              disabled={copyText.length === 0}
              iconOnly
              onClick={handleCopy}
              onMouseDown={(event) => event.preventDefault()}
              size="sm"
              variant="outline"
            >
              {isCopied ? (
                <Icons.check className="size-3.5" />
              ) : (
                <Icons.copy className="size-3.5" />
              )}
            </Button>
          )}
        </XStack>
      )}
      <YStack className="min-h-0 flex-1">
        <SharedMarkdownEditor
          value={copyText}
          onChange={setCopyText}
          onPreviewChange={updateAndPersistText}
          editable={canEdit}
          syncVersion={contentVersion}
          boundaryRef={blockRef}
          onEditEnd={persistPendingEdit}
          onAskSelection={onAskSelection}
          ariaLabel={bodyAriaLabel}
          className="flex min-h-0 flex-1 flex-col"
        />
        {showFooter && (
          <XStack className="justify-end gap-2 p-3 pt-0">
            {hasUserEdit ? (
              <Button
                aria-label="Reset text"
                className="bg-surface"
                iconOnly
                onClick={reset}
                onMouseDown={(event) => event.preventDefault()}
                size="sm"
                tooltip="Reset"
                variant="outline"
              >
                <Icons.refresh className="size-3.5" />
              </Button>
            ) : null}
            {isEmailAction ? (
              <Button
                aria-label="Open email draft"
                className="bg-surface"
                disabled={!canOpenEmail}
                onClick={handleEmailAction}
                onMouseDown={(event) => event.preventDefault()}
                size="sm"
                variant="outline"
              >
                <Icons.invite className="size-3.5" />
                Open in Email
              </Button>
            ) : null}
            {isCompact ? (
              <Button
                aria-label="Paste text"
                className="bg-surface"
                disabled={copyText.length === 0}
                onClick={handlePaste}
                onMouseDown={(event) => event.preventDefault()}
                size="sm"
                variant="outline"
              >
                <Icons.paste className="size-3.5" />
                Paste
              </Button>
            ) : null}
          </XStack>
        )}
      </YStack>
    </div>
  );
}
